<template>
  <div class="driver-invoice-list">
    <!-- Header Card -->
    <b-card no-body>
      <b-card-header class="d-flex justify-content-between align-items-center flex-wrap">
        <h4 class="mb-0">
          <feather-icon
            icon="FileTextIcon"
            size="20"
            class="mr-1"
          />
          Factures Livreurs
        </h4>
        <div
          v-if="isAdmin"
          class="d-flex flex-wrap"
          style="gap: 0.5rem"
        >
          <b-button
            variant="outline-primary"
            size="sm"
            @click="showBulkModal = true"
          >
            <feather-icon
              icon="UsersIcon"
              size="14"
              class="mr-50"
            />
            Génération en masse
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="showGenerateModal = true"
          >
            <feather-icon
              icon="PlusIcon"
              size="14"
              class="mr-50"
            />
            Générer une facture
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <!-- Filters -->
        <b-row class="mb-2">
          <b-col
            md="3"
            sm="6"
            class="mb-1"
          >
            <b-form-group
              label="Livreur"
              label-size="sm"
            >
              <b-form-select
                v-model="filters.driver"
                :options="driverOptions"
                size="sm"
                @change="loadInvoices"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-1"
          >
            <b-form-group
              label="Statut"
              label-size="sm"
            >
              <b-form-select
                v-model="filters.status"
                :options="statusOptions"
                size="sm"
                @change="loadInvoices"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-1"
          >
            <b-form-group
              label="Du"
              label-size="sm"
            >
              <b-form-input
                v-model="filters.start_date"
                type="date"
                size="sm"
                @change="loadInvoices"
              />
            </b-form-group>
          </b-col>
          <b-col
            md="3"
            sm="6"
            class="mb-1"
          >
            <b-form-group
              label="Au"
              label-size="sm"
            >
              <b-form-input
                v-model="filters.end_date"
                type="date"
                size="sm"
                @change="loadInvoices"
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Loading -->
        <div
          v-if="loading"
          class="text-center py-5"
        >
          <b-spinner variant="primary" />
          <p class="mt-2 text-muted">
            Chargement des factures...
          </p>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="invoices.length === 0"
          class="text-center py-5"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
            class="text-muted"
          />
          <p class="mt-2 text-muted">
            Aucune facture trouvée
          </p>
        </div>

        <!-- Table -->
        <b-table
          v-else
          :items="invoices"
          :fields="fields"
          responsive
          hover
          show-empty
          class="mb-0"
        >
          <template #cell(invoice_number)="data">
            <router-link
              :to="{ name: 'driver-invoice-detail', params: { id: data.item.id } }"
              class="font-weight-bold text-primary"
            >
              {{ data.item.invoice_number }}
            </router-link>
          </template>

          <template #cell(driver_name)="data">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="UserIcon"
                size="14"
                class="mr-50 text-muted"
              />
              {{ data.item.driver_name || data.item.driver_username || '-' }}
            </div>
          </template>

          <template #cell(period)="data">
            <small>
              {{ formatDate(data.item.start_date) }} — {{ formatDate(data.item.end_date) }}
            </small>
          </template>

          <template #cell(total_deliveries)="data">
            <span class="font-weight-bold">{{ data.item.total_deliveries }}</span>
            <small
              v-if="data.item.total_pickups > 0"
              class="text-muted ml-50"
            >
              + {{ data.item.total_pickups }} ramassages
            </small>
          </template>

          <template #cell(total_amount)="data">
            <span class="font-weight-bold text-success">
              {{ formatAmount(data.item.total_amount) }} DT
            </span>
          </template>

          <template #cell(status)="data">
            <b-badge :variant="statusVariant(data.item.status)">
              {{ statusLabel(data.item.status) }}
            </b-badge>
          </template>

          <template #cell(actions)="data">
            <b-dropdown
              size="sm"
              variant="outline-secondary"
              no-caret
              right
            >
              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="14"
                />
              </template>

              <b-dropdown-item :to="{ name: 'driver-invoice-detail', params: { id: data.item.id } }">
                <feather-icon
                  icon="EyeIcon"
                  size="14"
                  class="mr-50"
                />
                Voir détail
              </b-dropdown-item>

              <template v-if="isAdmin">
                <b-dropdown-divider v-if="data.item.status === 'draft'" />

                <b-dropdown-item
                  v-if="data.item.status === 'draft'"
                  @click="confirmStatus(data.item, 'confirmed')"
                >
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="14"
                    class="mr-50 text-info"
                  />
                  Confirmer
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="data.item.status === 'confirmed'"
                  @click="confirmStatus(data.item, 'paid')"
                >
                  <feather-icon
                    icon="DollarSignIcon"
                    size="14"
                    class="mr-50 text-success"
                  />
                  Marquer payée
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="data.item.status === 'draft' || data.item.status === 'confirmed'"
                  variant="warning"
                  @click="confirmCancel(data.item)"
                >
                  <feather-icon
                    icon="XCircleIcon"
                    size="14"
                    class="mr-50 text-warning"
                  />
                  Annuler
                </b-dropdown-item>

                <b-dropdown-item
                  v-if="data.item.status === 'draft'"
                  variant="danger"
                  @click="confirmDelete(data.item)"
                >
                  <feather-icon
                    icon="TrashIcon"
                    size="14"
                    class="mr-50 text-danger"
                  />
                  Supprimer
                </b-dropdown-item>
              </template>
            </b-dropdown>
          </template>
        </b-table>
      </b-card-body>
    </b-card>

    <!-- ============ Generate Single Invoice Modal ============ -->
    <b-modal
      v-model="showGenerateModal"
      title="Générer une Facture Livreur"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <b-tabs
        v-model="generateTab"
        content-class="mt-3"
        fill
      >
        <!-- Tab: Date Range -->
        <b-tab title="Par période">
          <b-form @submit.prevent="generateByRange">
            <b-form-group label="Livreur *">
              <b-form-select
                v-model="genForm.driver"
                :options="driverOptionsForGenerate"
                required
              />
            </b-form-group>
            <b-row>
              <b-col md="6">
                <b-form-group label="Date de début *">
                  <b-form-input
                    v-model="genForm.start_date"
                    type="date"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Date de fin *">
                  <b-form-input
                    v-model="genForm.end_date"
                    type="date"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <!-- Preview Button -->
            <b-button
              v-if="genForm.driver && genForm.start_date && genForm.end_date"
              variant="outline-info"
              size="sm"
              class="mb-3"
              :disabled="previewing"
              @click="loadPreview"
            >
              <feather-icon
                icon="SearchIcon"
                size="14"
                class="mr-50"
              />
              {{ previewing ? 'Chargement...' : 'Aperçu des colis éligibles' }}
            </b-button>

            <!-- Preview Result -->
            <b-alert
              v-if="preview"
              show
              variant="info"
              class="mb-3"
            >
              <div class="d-flex justify-content-between">
                <span><strong>Colis éligibles :</strong> {{ preview.summary.total_eligible }}</span>
                <span><strong>Disponibles :</strong> {{ preview.summary.available_for_invoicing }}</span>
              </div>
              <div
                v-if="preview.summary.delivery_commission_rate"
                class="mt-1"
              >
                <small>Taux livraison : {{ preview.summary.delivery_commission_rate }} DT/colis</small>
              </div>
            </b-alert>

            <div
              class="d-flex justify-content-end"
              style="gap: 0.5rem"
            >
              <b-button
                variant="outline-secondary"
                @click="showGenerateModal = false"
              >
                Annuler
              </b-button>
              <b-button
                type="submit"
                variant="primary"
                :disabled="generating || !genForm.driver || !genForm.start_date || !genForm.end_date"
              >
                <b-spinner
                  v-if="generating"
                  small
                  class="mr-50"
                />
                {{ generating ? 'Génération...' : 'Générer' }}
              </b-button>
            </div>
          </b-form>
        </b-tab>

        <!-- Tab: Monthly -->
        <b-tab title="Par mois">
          <b-form @submit.prevent="generateMonthly">
            <b-form-group label="Livreur *">
              <b-form-select
                v-model="monthlyForm.driver"
                :options="driverOptionsForGenerate"
                required
              />
            </b-form-group>
            <b-row>
              <b-col md="6">
                <b-form-group label="Mois *">
                  <b-form-select
                    v-model="monthlyForm.month"
                    :options="monthOptions"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="Année *">
                  <b-form-input
                    v-model.number="monthlyForm.year"
                    type="number"
                    :min="2020"
                    :max="2030"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <div
              class="d-flex justify-content-end"
              style="gap: 0.5rem"
            >
              <b-button
                variant="outline-secondary"
                @click="showGenerateModal = false"
              >
                Annuler
              </b-button>
              <b-button
                type="submit"
                variant="primary"
                :disabled="generating || !monthlyForm.driver || !monthlyForm.month || !monthlyForm.year"
              >
                <b-spinner
                  v-if="generating"
                  small
                  class="mr-50"
                />
                {{ generating ? 'Génération...' : 'Générer' }}
              </b-button>
            </div>
          </b-form>
        </b-tab>
      </b-tabs>
    </b-modal>

    <!-- ============ Bulk Generate Modal ============ -->
    <b-modal
      v-model="showBulkModal"
      title="Génération en masse"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <b-form @submit.prevent="generateBulk">
        <b-form-group label="Sélectionner les livreurs *">
          <b-form-checkbox-group
            v-model="bulkForm.driver_ids"
            :options="driverCheckboxOptions"
            stacked
            class="driver-checkbox-list"
          />
        </b-form-group>

        <b-row>
          <b-col md="6">
            <b-form-group label="Date de début *">
              <b-form-input
                v-model="bulkForm.start_date"
                type="date"
                required
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Date de fin *">
              <b-form-input
                v-model="bulkForm.end_date"
                type="date"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Bulk Results -->
        <b-alert
          v-if="bulkResults"
          show
          :variant="bulkResults.errors.length > 0 ? 'warning' : 'success'"
          class="mb-3"
        >
          <p class="mb-1">
            <strong>Résultat :</strong>
          </p>
          <div>✅ Générées : {{ bulkResults.successful.length }}</div>
          <div>⏭️ Ignorées : {{ bulkResults.skipped.length }}</div>
          <div v-if="bulkResults.errors.length > 0">
            ❌ Erreurs : {{ bulkResults.errors.length }}
          </div>
        </b-alert>

        <div
          class="d-flex justify-content-end"
          style="gap: 0.5rem"
        >
          <b-button
            variant="outline-secondary"
            @click="closeBulkModal"
          >
            {{ bulkResults ? 'Fermer' : 'Annuler' }}
          </b-button>
          <b-button
            v-if="!bulkResults"
            type="submit"
            variant="primary"
            :disabled="generating || bulkForm.driver_ids.length === 0 || !bulkForm.start_date || !bulkForm.end_date"
          >
            <b-spinner
              v-if="generating"
              small
              class="mr-50"
            />
            {{ generating ? 'Génération...' : `Générer (${bulkForm.driver_ids.length} livreurs)` }}
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BTable,
  BButton,
  BSpinner,
  BBadge,
  BRow,
  BCol,
  BFormGroup,
  BFormSelect,
  BFormInput,
  BForm,
  BModal,
  BAlert,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
  BTabs,
  BTab,
  BFormCheckboxGroup,
} from 'bootstrap-vue'
import driverInvoiceApi from '@/libs/driverInvoiceApi'

export default {
  name: 'DriverInvoiceList',
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BTable,
    BButton,
    BSpinner,
    BBadge,
    BRow,
    BCol,
    BFormGroup,
    BFormSelect,
    BFormInput,
    BForm,
    BModal,
    BAlert,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BTabs,
    BTab,
    BFormCheckboxGroup,
  },
  data() {
    return {
      loading: false,
      generating: false,
      previewing: false,
      invoices: [],
      drivers: [],
      preview: null,
      bulkResults: null,
      showGenerateModal: false,
      showBulkModal: false,
      generateTab: 0,
      filters: {
        driver: '',
        status: '',
        start_date: '',
        end_date: '',
      },
      genForm: {
        driver: '',
        start_date: '',
        end_date: '',
      },
      monthlyForm: {
        driver: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
      },
      bulkForm: {
        driver_ids: [],
        start_date: '',
        end_date: '',
      },
      statusOptions: [
        { value: '', text: 'Tous les statuts' },
        { value: 'draft', text: 'Brouillon' },
        { value: 'confirmed', text: 'Confirmée' },
        { value: 'paid', text: 'Payée' },
        { value: 'cancelled', text: 'Annulée' },
      ],
      monthOptions: [
        { value: '', text: 'Sélectionner...' },
        { value: 1, text: 'Janvier' },
        { value: 2, text: 'Février' },
        { value: 3, text: 'Mars' },
        { value: 4, text: 'Avril' },
        { value: 5, text: 'Mai' },
        { value: 6, text: 'Juin' },
        { value: 7, text: 'Juillet' },
        { value: 8, text: 'Août' },
        { value: 9, text: 'Septembre' },
        { value: 10, text: 'Octobre' },
        { value: 11, text: 'Novembre' },
        { value: 12, text: 'Décembre' },
      ],
      fields: [
        { key: 'invoice_number', label: 'N° Facture', sortable: true },
        { key: 'driver_name', label: 'Livreur' },
        { key: 'period', label: 'Période' },
        { key: 'total_deliveries', label: 'Livraisons', class: 'text-center' },
        {
          key: 'total_amount', label: 'Montant', class: 'text-right', sortable: true,
        },
        { key: 'status', label: 'Statut', class: 'text-center' },
        {
          key: 'actions', label: '', class: 'text-center', thStyle: { width: '80px' },
        },
      ],
    }
  },
  computed: {
    isAdmin() {
      const role = this.$store.state.user?.role || this.$store.state.role || localStorage.getItem('role')
      return ['admin', 'superadmin', 'admin_developer', 'accountant'].includes(role)
    },
    driverOptions() {
      const options = [{ value: '', text: 'Tous les livreurs' }]
      this.drivers.forEach(d => {
        options.push({ value: d.id, text: `${d.first_name || ''} ${d.last_name || d.username}`.trim() })
      })
      return options
    },
    driverOptionsForGenerate() {
      const options = [{ value: '', text: 'Sélectionner un livreur' }]
      this.drivers.forEach(d => {
        options.push({ value: d.id, text: `${d.first_name || ''} ${d.last_name || d.username}`.trim() })
      })
      return options
    },
    driverCheckboxOptions() {
      return this.drivers.map(d => ({
        value: d.id,
        text: `${d.first_name || ''} ${d.last_name || d.username}`.trim(),
      }))
    },
  },
  mounted() {
    this.loadDrivers()
    this.loadInvoices()
  },
  methods: {
    async loadDrivers() {
      try {
        this.drivers = await driverInvoiceApi.getAllDrivers()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading drivers:', error)
      }
    },

    async loadInvoices() {
      this.loading = true
      try {
        const params = {}
        if (this.filters.driver) params.driver = this.filters.driver
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.start_date) params.start_date_after = this.filters.start_date
        if (this.filters.end_date) params.end_date_before = this.filters.end_date
        const response = await driverInvoiceApi.list(params)
        this.invoices = response.data.results || response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading invoices:', error)
      } finally {
        this.loading = false
      }
    },

    async loadPreview() {
      this.previewing = true
      this.preview = null
      try {
        const response = await driverInvoiceApi.previewEligible({
          driver: this.genForm.driver,
          start_date: this.genForm.start_date,
          end_date: this.genForm.end_date,
        })
        this.preview = response.data
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors du chargement de l\'aperçu'
        this.toast(msg, 'danger')
      } finally {
        this.previewing = false
      }
    },

    async generateByRange() {
      if (this.genForm.start_date > this.genForm.end_date) {
        this.toast('La date de début doit précéder la date de fin.', 'danger')
        return
      }
      this.generating = true
      try {
        await driverInvoiceApi.generate({
          driver: this.genForm.driver,
          start_date: this.genForm.start_date,
          end_date: this.genForm.end_date,
        })
        this.toast('Facture générée avec succès', 'success')
        this.showGenerateModal = false
        this.resetGenForm()
        this.loadInvoices()
      } catch (error) {
        const msg = error.response?.data?.error || error.response?.data?.detail || 'Erreur lors de la génération'
        this.toast(msg, 'danger')
      } finally {
        this.generating = false
      }
    },

    async generateMonthly() {
      this.generating = true
      try {
        await driverInvoiceApi.generateMonthly({
          driver: this.monthlyForm.driver,
          month: this.monthlyForm.month,
          year: this.monthlyForm.year,
        })
        this.toast('Facture mensuelle générée avec succès', 'success')
        this.showGenerateModal = false
        this.resetMonthlyForm()
        this.loadInvoices()
      } catch (error) {
        const msg = error.response?.data?.error || error.response?.data?.detail || 'Erreur lors de la génération'
        this.toast(msg, 'danger')
      } finally {
        this.generating = false
      }
    },

    async generateBulk() {
      this.generating = true
      this.bulkResults = null
      try {
        const response = await driverInvoiceApi.generateBulk({
          driver_ids: this.bulkForm.driver_ids,
          start_date: this.bulkForm.start_date,
          end_date: this.bulkForm.end_date,
        })
        this.bulkResults = response.data
        this.loadInvoices()
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de la génération en masse'
        this.toast(msg, 'danger')
      } finally {
        this.generating = false
      }
    },

    async confirmStatus(invoice, newStatus) {
      const labels = { confirmed: 'confirmer', paid: 'marquer comme payée' }
      const ok = await this.$bvModal.msgBoxConfirm(
        `Voulez-vous ${labels[newStatus]} la facture ${invoice.invoice_number} ?`,
        {
          title: 'Confirmation',
          okVariant: newStatus === 'paid' ? 'success' : 'primary',
          okTitle: 'Oui',
          cancelTitle: 'Non',
        },
      )
      if (!ok) return
      try {
        await driverInvoiceApi.updateStatus(invoice.id, newStatus)
        this.toast('Statut mis à jour', 'success')
        this.loadInvoices()
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de la mise à jour'
        this.toast(msg, 'danger')
      }
    },

    async confirmCancel(invoice) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Voulez-vous annuler la facture ${invoice.invoice_number} ? Les colis redeviendront éligibles.`,
        {
          title: 'Annuler la facture',
          okVariant: 'warning',
          okTitle: 'Oui, annuler',
          cancelTitle: 'Non',
        },
      )
      if (!ok) return
      try {
        await driverInvoiceApi.cancel(invoice.id)
        this.toast('Facture annulée', 'success')
        this.loadInvoices()
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de l\'annulation'
        this.toast(msg, 'danger')
      }
    },

    async confirmDelete(invoice) {
      const ok = await this.$bvModal.msgBoxConfirm(
        `Voulez-vous supprimer définitivement la facture ${invoice.invoice_number} ?`,
        {
          title: 'Supprimer la facture',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
        },
      )
      if (!ok) return
      try {
        await driverInvoiceApi.delete(invoice.id)
        this.toast('Facture supprimée', 'success')
        this.loadInvoices()
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de la suppression'
        this.toast(msg, 'danger')
      }
    },

    closeBulkModal() {
      this.showBulkModal = false
      this.bulkResults = null
      this.bulkForm = { driver_ids: [], start_date: '', end_date: '' }
    },

    resetGenForm() {
      this.genForm = { driver: '', start_date: '', end_date: '' }
      this.preview = null
    },

    resetMonthlyForm() {
      this.monthlyForm = { driver: '', month: new Date().getMonth() + 1, year: new Date().getFullYear() }
    },

    statusVariant(status) {
      const map = {
        draft: 'light-secondary',
        confirmed: 'light-info',
        paid: 'light-success',
        cancelled: 'light-danger',
      }
      return map[status] || 'secondary'
    },

    statusLabel(status) {
      const map = {
        draft: 'Brouillon',
        confirmed: 'Confirmée',
        paid: 'Payée',
        cancelled: 'Annulée',
      }
      return map[status] || status
    },

    formatAmount(value) {
      if (value === null || value === undefined) return '0.000'
      return parseFloat(value).toFixed(3)
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('fr-FR')
    },

    toast(message, variant) {
      this.$bvToast.toast(message, {
        title: variant === 'success' ? 'Succès' : 'Erreur',
        variant,
        solid: true,
        autoHideDelay: 4000,
      })
    },
  },
}
</script>

<style scoped>
.driver-checkbox-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 0.357rem;
  padding: 0.75rem;
}
</style>
